import { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Brush,
    ResponsiveContainer,
    ReferenceLine,
} from 'recharts';
import { AggregatedReturnsTooltip } from './AggregatedReturnsTooltip';
import { AggregatedReturnsEmptyState } from './AggregatedReturnsEmptyState';
import { AggregatedReturnsTable } from './AggregatedReturnsTable';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { Spinner } from '@/components/ui/Spinner';
import { ChartTableToggle } from '@/components/ui/ChartTableToggle';
import { DownloadMenu } from '@/components/ui/DownloadMenu';
import {
    PrivacyAwareYAxisTick,
    PrivacyBlurFilter,
    usePrivacyBlurFilterId,
} from '@/components/charts/PrivacyAwareYAxisTick';
import { useAggregatedMwrr } from '@/api/hooks/useLedgerAnalytics';
import { usePrivacy } from '@/contexts/PrivacyContext';
import { getErrorMessage } from '@/utils/errorHandler';
import { CHART_COLORS } from '@/utils/chartColors';
import { formatDateISO } from '@/utils/date';
import { TOGGLE_STYLES, TOGGLE_BUTTON_BASE } from '@/styles/toggleStyles';
import type { ColumnDefinition } from '@/lib/export';
import type { AnalyticsGranularity, AnalyticsMode, AggregatedMwrrItem } from '@/api/types';

interface BrushRange {
    startIndex: number;
    endIndex: number;
}

interface AggregatedReturnsChartProps {
    /** Collection IDs to filter by (empty = all) */
    collectionIds: number[];
    /** Tag IDs to filter by (empty = all) */
    tagIds: number[];
    /** Callback when filters should be reset */
    onResetFilters?: () => void;
    /** Current analytics mode (mwrr) */
    mode: 'mwrr';
    /** Callback when mode changes (enables mode toggle in header) */
    onModeChange?: (mode: AnalyticsMode) => void;
    /** Current granularity (month/year) - controlled by parent */
    granularity: AnalyticsGranularity;
    /** Callback when granularity changes */
    onGranularityChange: (granularity: AnalyticsGranularity) => void;
    /** Current view mode (chart/table) - controlled by parent */
    viewMode: 'chart' | 'table';
    /** Callback when view mode changes */
    onViewModeChange: (viewMode: 'chart' | 'table') => void;
    /** Callback when fetching state changes */
    onFetchingChange?: (isFetching: boolean) => void;
}

/**
 * Calculate responsive chart height based on container width.
 */
function getChartHeight(width: number): number {
    if (width < 640) return 300;
    if (width <= 1024) return 350;
    return 400;
}

/**
 * Format Y-axis percentage values.
 */
function formatYAxisValue(value: number): string {
    return `${value.toFixed(0)}%`;
}

/**
 * Get Y-axis width based on container width.
 */
function getYAxisWidth(containerWidth: number): number {
    return containerWidth < 640 ? 45 : 55;
}

/**
 * Column definitions for aggregated MWRR export.
 */
const AGGREGATED_MWRR_EXPORT_COLUMNS: ColumnDefinition[] = [
    { key: 'periodLabel', label: 'Period', format: 'date' },
    { key: 'mwrr', label: 'MWRR %', format: 'percent' },
];

/**
 * Aggregated returns chart component for portfolio-level analytics.
 * Displays aggregated MWRR across multiple ledgers with collection/tag filters.
 * Includes granularity toggle (month/year), chart/table toggle, download menu, and brush for date range selection.
 *
 * Notes:
 * - Only works for assets (returns are not meaningful for debts)
 * - Shows an info banner when some ledgers are excluded
 * - Uses line chart (not area) for percentage-based data
 */
export function AggregatedReturnsChart({
    collectionIds,
    tagIds,
    onResetFilters,
    mode,
    onModeChange,
    granularity,
    onGranularityChange,
    viewMode,
    onViewModeChange,
    onFetchingChange,
}: AggregatedReturnsChartProps) {
    const { isPrivacyModeEnabled } = usePrivacy();
    const [brushRange, setBrushRange] = useState<BrushRange | null>(null);
    const [containerWidth, setContainerWidth] = useState<number>(800);
    const containerRef = useRef<HTMLDivElement>(null);

    // Metric label for display
    const metricLabel = 'MWRR';

    const hasFilters = collectionIds.length > 0 || tagIds.length > 0;

    // Fetch MWRR data
    const {
        data: response,
        isLoading,
        isFetching,
        error,
        isError,
        refetch,
    } = useAggregatedMwrr({
        granularity,
        collectionIds,
        tagIds,
    });

    // Measure container width
    useEffect(() => {
        const updateWidth = () => {
            if (containerRef.current) {
                setContainerWidth(containerRef.current.offsetWidth);
            }
        };

        updateWidth();
        window.addEventListener('resize', updateWidth);
        return () => window.removeEventListener('resize', updateWidth);
    }, []);

    // Reset brush range when granularity or filters change
    useEffect(() => {
        setBrushRange(null);
    }, [granularity, collectionIds, tagIds, mode]);

    // Report fetching state to parent (only during refetch, not initial load)
    useEffect(() => {
        onFetchingChange?.(isFetching && !isLoading);
    }, [isFetching, isLoading, onFetchingChange]);

    const chartHeight = getChartHeight(containerWidth);
    const brushHeight = 40;
    const yAxisWidth = getYAxisWidth(containerWidth);

    // Transform data for chart
    const chartData = useMemo(() => {
        if (!response?.items) return [];

        return (response.items as AggregatedMwrrItem[]).map((item) => ({
            ...item,
            name: item.periodLabel,
            chartValue: item.mwrr,
        }));
    }, [response?.items]);

    // Filter out null values for Y-axis domain calculation
    const validData = chartData.filter((d) => d.chartValue !== null);
    const showBrush = chartData.length >= 4;

    // Check if all values are null (insufficient data)
    const allValuesNull = validData.length === 0 && chartData.length > 0;

    // Calculate min/max for Y-axis domain (include zero for reference line)
    const yDomain = useMemo(() => {
        if (validData.length === 0) return [-10, 10];
        const values = validData.map((d) => d.chartValue as number);
        const min = Math.min(0, ...values);
        const max = Math.max(0, ...values);
        // Add some padding
        const padding = Math.max(Math.abs(max - min) * 0.1, 5);
        return [min - padding, max + padding];
    }, [validData]);

    // Handle brush change
    const handleBrushChange = useCallback(
        (brushState: { startIndex?: number; endIndex?: number } | null) => {
            if (!brushState || brushState.startIndex === undefined || brushState.endIndex === undefined) {
                setBrushRange(null);
                return;
            }
            setBrushRange({
                startIndex: brushState.startIndex,
                endIndex: brushState.endIndex,
            });
        },
        []
    );

    // Color scheme - use same color as assets (indigo)
    const colors = CHART_COLORS.asset;
    const blurFilterId = usePrivacyBlurFilterId();

    // Export filename and columns
    const exportFilename = `aggregated-${mode}-${formatDateISO()}`;
    const exportColumns = AGGREGATED_MWRR_EXPORT_COLUMNS;

    // Error state with retry
    if (isError) {
        return (
            <div className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary p-4">
                <ErrorAlert message={getErrorMessage(error)} />
                <div className="mt-4 text-center">
                    <button
                        onClick={() => refetch()}
                        className="px-4 py-2 text-sm font-medium text-accent hover:text-accent/80 transition-colors"
                    >
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    // Empty state (only after loading completes with no data or all null values)
    const hasNoData = !isLoading && response && (response.items.length === 0 || allValuesNull);
    if (hasNoData) {
        return (
            <div
                ref={containerRef}
                className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary"
            >
                {/* Header with controls - mobile: 2 rows, desktop: single row */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                        {/* Left group: Mode | Granularity (separator on desktop only) */}
                        <div className="flex items-center justify-between sm:justify-start gap-3">
                            {onModeChange ? (
                                <ModeToggle mode={mode} onChange={onModeChange} />
                            ) : (
                                <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                    {metricLabel}
                                </div>
                            )}
                            <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                <GranularityToggle
                                    granularity={granularity}
                                    onChange={onGranularityChange}
                                />
                            </div>
                        </div>

                        {/* Right group: Chart/Table | Download (separator on desktop only) */}
                        <div className="flex items-center justify-between sm:justify-end gap-3">
                            <ChartTableToggle
                                value={viewMode}
                                onChange={onViewModeChange}
                                disabled
                            />
                            <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                                <DownloadMenu
                                    data={response?.items || []}
                                    columns={exportColumns}
                                    filename={exportFilename}
                                    disabled
                                />
                            </div>
                        </div>
                    </div>
                </div>

                <AggregatedReturnsEmptyState
                    hasFilters={hasFilters}
                    onResetFilters={onResetFilters}
                    allLedgersExcluded={false}
                    excludedCount={0}
                    allValuesNull={allValuesNull}
                    metricLabel={metricLabel}
                    minHeight={chartHeight}
                />
            </div>
        );
    }

    return (
        <div
            ref={containerRef}
            className="rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-secondary"
        >
            {/* Header with controls - mobile: 2 rows, desktop: single row */}
            <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                    {/* Left group: Mode | Granularity (separator on desktop only) */}
                    <div className="flex items-center justify-between sm:justify-start gap-3">
                        {onModeChange ? (
                            <ModeToggle mode={mode} onChange={onModeChange} />
                        ) : (
                            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
                                {metricLabel}
                            </div>
                        )}
                        <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                            <GranularityToggle
                                granularity={granularity}
                                onChange={onGranularityChange}
                            />
                        </div>
                    </div>

                    {/* Right group: Chart/Table | Download (separator on desktop only) */}
                    <div className="flex items-center justify-between sm:justify-end gap-3">
                        <ChartTableToggle
                            value={viewMode}
                            onChange={onViewModeChange}
                        />
                        <div className="sm:pl-3 sm:border-l sm:border-gray-200 sm:dark:border-gray-600">
                            <DownloadMenu
                                data={response?.items || []}
                                columns={exportColumns}
                                filename={exportFilename}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Content area - conditionally render chart or table */}
            {viewMode === 'table' ? (
                <div className="relative">
                    {/* Loading overlay for table view */}
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center z-10 bg-white/50 dark:bg-gray-900/50">
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                                <Spinner size="sm" />
                                <span className="text-sm text-gray-600 dark:text-gray-300">Loading...</span>
                            </div>
                        </div>
                    )}
                    <AggregatedReturnsTable
                        items={response?.items || []}
                    />
                </div>
            ) : (
                <div
                    className="relative w-full chart-container px-4 pb-4"
                    style={{ height: chartHeight + (showBrush ? brushHeight + 20 : 0) }}
                >
                    <style>{`
                        .chart-container .recharts-cartesian-grid-horizontal line,
                        .chart-container .recharts-cartesian-grid-vertical line {
                            stroke: #e5e7eb;
                        }
                        .dark .chart-container .recharts-cartesian-grid-horizontal line,
                        .dark .chart-container .recharts-cartesian-grid-vertical line {
                            stroke: #374151;
                        }
                        .chart-container .recharts-brush-slide {
                            fill: ${colors.fill};
                            fill-opacity: 0.2;
                        }
                        .dark .chart-container .recharts-brush-slide {
                            fill: ${colors.fill};
                            fill-opacity: 0.3;
                        }
                        .chart-container .recharts-brush-traveller rect {
                            fill: ${colors.stroke};
                        }
                    `}</style>
                    {/* Loading overlay */}
                    {isLoading && (
                        <div className="absolute inset-0 flex items-center justify-center z-10">
                            <div className="flex items-center space-x-2 bg-white dark:bg-gray-800 px-4 py-2 rounded-lg shadow-lg">
                                <Spinner size="sm" />
                                <span className="text-sm text-gray-600 dark:text-gray-300">Loading...</span>
                            </div>
                        </div>
                    )}
                    <ResponsiveContainer width="100%" height="100%">
                        <LineChart
                            data={chartData}
                            margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <PrivacyBlurFilter id={blurFilterId} />
                            </defs>
                            <CartesianGrid
                                strokeDasharray="3 3"
                                vertical={false}
                            />
                            <XAxis
                                dataKey="name"
                                tick={{ fontSize: 12 }}
                                tickLine={false}
                                axisLine={false}
                                dy={10}
                                className="text-gray-600 dark:text-gray-400"
                            />
                            <YAxis
                                key={String(isPrivacyModeEnabled)}
                                tick={
                                    <PrivacyAwareYAxisTick
                                        formatValue={formatYAxisValue}
                                        blurred={isPrivacyModeEnabled}
                                        blurFilterId={blurFilterId}
                                    />
                                }
                                tickLine={false}
                                axisLine={false}
                                width={yAxisWidth}
                                domain={yDomain}
                                tickFormatter={formatYAxisValue}
                            />
                            {/* Zero reference line */}
                            <ReferenceLine
                                y={0}
                                stroke="#9ca3af"
                                strokeDasharray="3 3"
                                strokeWidth={1}
                            />
                            {response && (
                                <Tooltip
                                    content={<AggregatedReturnsTooltip />}
                                    cursor={{ stroke: colors.stroke, strokeWidth: 1, strokeDasharray: '3 3' }}
                                />
                            )}
                            <Line
                                type="monotone"
                                dataKey="chartValue"
                                stroke={colors.stroke}
                                strokeWidth={2}
                                dot={{ r: 3, fill: colors.stroke }}
                                activeDot={{ r: 5, fill: colors.stroke }}
                                animationDuration={300}
                                animationEasing="ease-out"
                                connectNulls={false}
                            />
                            {showBrush && (
                                <Brush
                                    dataKey="name"
                                    height={brushHeight}
                                    stroke={colors.stroke}
                                    fill="transparent"
                                    onChange={handleBrushChange}
                                    startIndex={brushRange?.startIndex}
                                    endIndex={brushRange?.endIndex}
                                    travellerWidth={10}
                                >
                                    <LineChart data={chartData}>
                                        <Line
                                            type="monotone"
                                            dataKey="chartValue"
                                            stroke={colors.stroke}
                                            strokeWidth={1}
                                            dot={false}
                                        />
                                    </LineChart>
                                </Brush>
                            )}
                        </LineChart>
                    </ResponsiveContainer>
                </div>
            )}
        </div>
    );
}

/**
 * Granularity toggle component for switching between month and year views.
 */
interface GranularityToggleProps {
    granularity: AnalyticsGranularity;
    onChange: (granularity: AnalyticsGranularity) => void;
    disabled?: boolean;
}

function GranularityToggle({ granularity, onChange, disabled = false }: GranularityToggleProps) {
    return (
        <div className={TOGGLE_STYLES.container}>
            <button
                onClick={() => onChange('month')}
                disabled={disabled}
                className={`w-10 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE} ${
                    granularity === 'month'
                        ? TOGGLE_STYLES.active
                        : TOGGLE_STYLES.inactive
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                M
            </button>
            <button
                onClick={() => onChange('year')}
                disabled={disabled}
                className={`w-10 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE} ${
                    granularity === 'year'
                        ? TOGGLE_STYLES.active
                        : TOGGLE_STYLES.inactive
                } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
                Y
            </button>
        </div>
    );
}

/**
 * Mode toggle component for switching between Value, P&L, and MWRR views.
 * Returns chart is only for assets, so only relevant options are shown.
 */
interface ModeToggleProps {
    mode: 'mwrr';
    onChange: (mode: AnalyticsMode) => void;
}

function ModeToggle({ mode, onChange }: ModeToggleProps) {
    const options: { value: AnalyticsMode; label: string }[] = [
        { value: 'value', label: 'Value' },
        { value: 'pnl', label: 'P&L' },
        { value: 'mwrr', label: 'MWRR' },
    ];

    return (
        <div className={TOGGLE_STYLES.container}>
            {options.map((option) => (
                <button
                    key={option.value}
                    onClick={() => onChange(option.value)}
                    className={`px-4 py-1.5 text-sm font-medium ${TOGGLE_BUTTON_BASE} ${
                        mode === option.value
                            ? TOGGLE_STYLES.active
                            : TOGGLE_STYLES.inactive
                    }`}
                >
                    {option.label}
                </button>
            ))}
        </div>
    );
}
